import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUpdateOnboardingWorker } from "../../../api/useUpdateOnboardingWorker";
import { ONBOARDING_STAGES } from "../../../util/constants";
import { formatName } from "../../../util/name";

export const NAME_MAX_LENGTH = 50;

const nameStepFormSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, {
        message: "Please fill out your legal first name to continue sign up",
      })
      .max(NAME_MAX_LENGTH, {
        message: `First name must be ${NAME_MAX_LENGTH} characters or less`,
      }),
    middleName: z
      .string()
      .trim()
      .max(NAME_MAX_LENGTH, {
        message: `Middle name must be ${NAME_MAX_LENGTH} characters or less`,
      }),
    lastName: z
      .string()
      .trim()
      .min(1, {
        message: "Please fill out your legal last name to continue sign up",
      })
      .max(NAME_MAX_LENGTH, {
        message: `Last name must be ${NAME_MAX_LENGTH} characters or less`,
      }),
    noMiddleNameConfirmationChecked: z.boolean(),
  })
  .refine(
    (data) =>
      data.noMiddleNameConfirmationChecked ||
      (data.middleName && data.middleName.trim().length > 0),
    { message: "Please fill out your legal middle name to continue sign up", path: ["middleName"] }
  );

interface UseNameStepProps {
  onNext: () => void;
  worker?: Worker;
}

export function useNameStep(props: UseNameStepProps) {
  const { onNext, worker } = props;
  const [didUserConfirmNoMiddleName, setDidUserConfirmNoMiddleName] = useLocalStorage(
    `onboarding-name-step-no-middle-name-confirmation-checked-${worker?.userId}`,
    false
  );
  const noMiddleNameSaved =
    isDefined(worker?.middleName) && worker?.middleName?.trim().length === 0;

  const form = useForm({
    values: {
      firstName: worker?.firstName ?? "",
      middleName: worker?.middleName ?? "",
      lastName: worker?.lastName ?? "",
      noMiddleNameConfirmationChecked: didUserConfirmNoMiddleName && noMiddleNameSaved,
    },
    resolver: zodResolver(nameStepFormSchema),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { isSubmitting },
  } = form;
  const noMiddleNameConfirmationChecked = watch("noMiddleNameConfirmationChecked");

  const { showErrorToast } = useToast();

  const { mutateAsync: updateOnboardingWorker } = useUpdateOnboardingWorker();

  const updateWorkerCache = useUpdateWorkerCache();

  function onNameChange(value: string, key: "firstName" | "middleName" | "lastName") {
    // Using trimMode: "start" to only remove leading spaces, not trailing ones.
    // Previously, Ionic input behaved differently, but this input now sets the exact
    // value that is in the state. This allows users to add trailing spaces.
    // normalizeHyphens: false to allow users to type spaces around hyphens while typing,
    // hyphen normalization happens on blur and submit.
    const formattedName = formatName(value, { trimMode: "start", normalizeHyphens: false });
    setValue(key, formattedName);
    // Clear any existing errors for this field when user starts typing
    clearErrors(key);
  }

  function onNameBlur(value: string, key: "firstName" | "middleName" | "lastName") {
    setValue(key, formatName(value));
  }

  function onNoMiddleNameConfirmationChange(value: boolean) {
    setValue("noMiddleNameConfirmationChecked", value);
    if (value) {
      setValue("middleName", "");
      // Clear middle name error when user confirms they don't have a middle name
      clearErrors("middleName");
    }
  }

  const onContinue = handleSubmit(async (formData) => {
    try {
      const updatedNameData = {
        firstName: formatName(formData.firstName),
        middleName: formData.noMiddleNameConfirmationChecked ? "" : formatName(formData.middleName),
        lastName: formatName(formData.lastName),
      };

      const cognitoToken = await getCognitoSingleton().getIdToken();

      if (!isDefined(cognitoToken)) {
        throw new Error("No authentication token available");
      }

      await updateOnboardingWorker({
        stage: ONBOARDING_STAGES.NAME,
        signupFlowVersion: "V2",
        firstName: updatedNameData.firstName,
        middleName: updatedNameData.middleName,
        lastName: updatedNameData.lastName,
        requestManualOigValidation: true,
      });

      setDidUserConfirmNoMiddleName(Boolean(noMiddleNameConfirmationChecked));

      updateWorkerCache({
        ...worker,
        firstName: updatedNameData.firstName,
        middleName: updatedNameData.middleName,
        lastName: updatedNameData.lastName,
      });

      logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
        workerId: worker?.userId,
        stepCompleted: ONBOARDING_STAGES.NAME,
        firstName: updatedNameData.firstName,
        lastName: updatedNameData.lastName,
        middleName: updatedNameData.middleName,
        noMiddleNameConfirmationChecked: formData.noMiddleNameConfirmationChecked,
        timestamp: new Date().toISOString(),
      });

      onNext();
    } catch (error) {
      logError(APP_V2_APP_EVENTS.ONBOARDING_UPDATE_PERSONAL_INFORMATION_ERROR, {
        error,
        metadata: {
          workerId: worker?.userId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          middleName: formData.middleName,
          noMiddleNameConfirmationChecked: formData.noMiddleNameConfirmationChecked,
        },
      });

      showErrorToast("Unexpected error occurred while saving personal information");
    }
  });

  return {
    control,
    handleSubmit: onContinue,
    onNameChange,
    onNameBlur,
    noMiddleNameConfirmationChecked,
    isSubmitting,
    onNoMiddleNameConfirmationChange,
  };
}
